// A quién le aplica una promo de venta: solo con crédito SureKeep o con cualquier forma de pago,
// y las condiciones del cliente que pide. Puro, sin React.
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import type { CatalogItem, ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import type {
    PromotionAppliesTo,
    RetailPromotionsErrorCode,
    SalePromotionConditions,
} from '../api/wizardPromotionsContract';
import type { PromotionPaymentOption } from '@/domains/retail/admin/promotions/api/promotionsContract';
import { paymentOptionsShortText } from '@/domains/retail/admin/promotions/lib/salePromotionFormOptions';

const SUREKEEP_ONLY_CHIP = 'Solo SureKeep';

/** Chip de forma de pago: «Solo SureKeep o efectivo»; `null` sin restricción. Sin la lista cae a `appliesTo`. */
export function paymentOptionsChipLabel(promo: {
    appliesTo: PromotionAppliesTo;
    paymentOptions?: readonly PromotionPaymentOption[] | null;
}): string | null {
    if (promo.paymentOptions === undefined)
        return isSureKeepOnly(promo) ? SUREKEEP_ONLY_CHIP : null;
    return promo.paymentOptions?.length
        ? `Solo ${paymentOptionsShortText(promo.paymentOptions)}`
        : null;
}

/** Forma de pago de la promo tal cual, para copiarla a otra forma del contrato. */
export function paymentFieldsOf(promo: {
    appliesTo: PromotionAppliesTo;
    paymentOptions?: PromotionPaymentOption[] | null;
}): { appliesTo: PromotionAppliesTo; paymentOptions?: PromotionPaymentOption[] | null } {
    return promo.paymentOptions === undefined
        ? { appliesTo: promo.appliesTo }
        : { appliesTo: promo.appliesTo, paymentOptions: promo.paymentOptions };
}

export function isSureKeepOnly(promo: { appliesTo: PromotionAppliesTo }): boolean {
    return promo.appliesTo === 'SUREKEEP_ONLY';
}

type FinanceFields = Pick<CatalogItem, 'eligibleFinanciers' | 'financeable' | 'affordableOnCredit'>;

/** Una promo solo SureKeep no le toca a un equipo que SureKeep no financia (aunque lo tome otra financiera). */
export function appliesToItem(
    item: FinanceFields,
    promo: { appliesTo: PromotionAppliesTo },
): boolean {
    return !isSureKeepOnly(promo) || sureKeepFinances(item);
}

/** «Una vez por cliente · Requiere crédito SureKeep activo · Solo para su segmento»; `null` sin condiciones. */
export function conditionsText(
    conditions: SalePromotionConditions | undefined,
    blockedBy?: RetailPromotionsErrorCode | null,
): string | null {
    const parts: string[] = [];
    if (conditions?.oncePerCustomer) parts.push('Una vez por cliente');
    if (conditions?.requiresActiveSurekeepCredit) parts.push('Requiere crédito SureKeep activo');
    if (conditions?.segmentIds && conditions.segmentIds.length > 0) {
        parts.push('Solo para su segmento');
    }
    if (blockedBy === 'CUSTOMER_NOT_IDENTIFIED')
        parts.push('Identifica al cliente para confirmarla');
    return parts.length > 0 ? parts.join(' · ') : null;
}

/** Promos de la tienda que le tocan a la venta: sin las solo SureKeep si SureKeep no financia el equipo. */
export function forAnchor<T extends { appliesTo: PromotionAppliesTo }>(
    promotions: readonly T[],
    anchorWithoutSureKeep: boolean,
): T[] {
    return anchorWithoutSureKeep ? promotions.filter((p) => !isSureKeepOnly(p)) : [...promotions];
}

/** Origen de un descuento: si la promo o el código solo aplican con SureKeep. */
export interface SureKeepOnlyOrigins {
    promotion: (promotionId: string) => boolean;
    /** El preview del código aún no trae `appliesTo`: por ahora ninguno cuenta. */
    code?: (code: string) => boolean;
}

/** Lo que bajan en las líneas vivas las promos y códigos solo SureKeep; de contado no va. */
export function sureKeepOnlyDiscount(
    lines: readonly {
        promotionId?: string;
        discountCode?: string;
        discountCents?: number;
        status?: string;
    }[],
    origins: SureKeepOnlyOrigins,
): number {
    const codeIsSureKeep = origins.code ?? (() => false);
    return lines
        .filter((l) => l.status !== 'RELEASING' && (l.discountCents ?? 0) > 0)
        .filter(
            (l) =>
                (l.promotionId !== undefined && origins.promotion(l.promotionId)) ||
                (l.discountCode !== undefined && codeIsSureKeep(l.discountCode)),
        )
        .reduce((sum, l) => sum + (l.discountCents ?? 0), 0);
}

/** La venta va sin SureKeep: el cliente no califica o SureKeep no financia el equipo. Con nivel pendiente, sí va. */
export function saleWithoutSureKeep(
    session: WizardSession,
    anchor: ReservedLine | undefined = anchorLineOf(session),
): boolean {
    if (session.loan?.level === 'NOT_ELIGIBLE') return true;
    return anchor ? !sureKeepFinances(anchor) : false;
}
